/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: RpLogicReport detail component with overview, pipeline visualization, configuration, execution and output field tabs
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {QlService} from '../../../../../core/shared/ql/service/ql.service';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {RpLogicReportService} from '../../../service/api/rp-logic-report.service';
import {RpLogicReportSelectionParamService} from '../../../service/api/rp-logic-report-selection-param.service';
import {RpLogicReportStepDto} from '../../../model/dto/entity/rp-logic-report-step.dto';
import {RpLogicReportStepMappingDto} from '../../../model/dto/entity/rp-logic-report-step-mapping.dto';
import {RpLogicReportSelectionParamDto} from '../../../model/dto/entity/rp-logic-report-selection-param.dto';
import {RpLogicReportOutputFieldDto} from '../../../model/dto/entity/rp-logic-report-output-field.dto';
import {RpLogicReportExecutionResultDto} from '../../../model/dto/api/rp-logic-report-execution-result.dto';
import {RpLogicReportSelectionFormDto} from '../../../model/dto/api/rp-logic-report-selection-form.dto';
import {RpLogicReportSelectionFieldDto} from '../../../model/dto/api/rp-logic-report-selection-field.dto';
import {RpLogicReportExportResultDto} from '../../../model/dto/api/rp-logic-report-export-result.dto';
import {RpLogicReportStatusEnum} from '../../../enum/rp-logic-report-status.enum';
import {RpLogicReportStepTypeEnum} from '../../../enum/rp-logic-report-step-type.enum';
import {PipelineNode} from '../../../interface/pipeline-node.interface';

@Component({
    selector: 'rp-logic-report-object-component',
    templateUrl: './rp-logic-report-object.component.html',
    styleUrls: ['./rp-logic-report-object.component.scss'],
    standalone: false
})
export class RpLogicReportObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    // Expose enums for template
    readonly RpLogicReportStepTypeEnum = RpLogicReportStepTypeEnum;
    readonly RpLogicReportStatusEnum = RpLogicReportStatusEnum;

    // Tab navigation (replaces navigationItems)
    activeTabIndex: number = 0;

    // Widgets
    overviewWidget: WidgetData;
    stepsWidget: WidgetData;
    mappingsWidget: WidgetData;
    selectionParamsWidget: WidgetData;
    outputFieldsWidget: WidgetData;

    selectedStepId: number;

    // Pipeline visualization data
    pipelineSteps: RpLogicReportStepDto[] = [];
    pipelineMappings: RpLogicReportStepMappingDto[] = [];
    pipelineNodes: PipelineNode[] = [];
    selectionParamEntries: RpLogicReportSelectionParamDto[] = [];
    outputFieldEntries: RpLogicReportOutputFieldDto[] = [];
    pipelineLoading: boolean = false;
    selectedNode: PipelineNode | null = null;

    // Execution state
    executionResult: RpLogicReportExecutionResultDto;
    executionBusy: boolean = false;
    executionError: string;
    finalResultColumns: string[] = [];
    pipelineContextAliases: string[] = [];

    /** Selection form metadata loaded from backend */
    selectionForm: RpLogicReportSelectionFormDto;
    /** Dynamic form group built from selection form fields */
    selectionFormGroup: FormGroup;
    /** Structured export result with columns and data */
    exportResult: RpLogicReportExportResultDto;
    /** Loading indicator for selection form */
    selectionFormLoading: boolean = false;
    /** Flag indicating the selection form has been loaded */
    selectionFormLoaded: boolean = false;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private logicReportService: RpLogicReportService,
        private selectionParamService: RpLogicReportSelectionParamService,
        private qlService: QlService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    override onObjectChanged(): void {
        this.createWidgets();

        // Reset and eagerly load the selection form so it is ready when the user navigates to the execution tab
        this.selectionFormLoaded = false;
        this.loadSelectionForm();

        // Load pipeline data for the Pipeline tab
        this.loadPipelineData();
    }

    private createWidgets(): void {
        const logicReportId = this.objectIdentifier.objectId;

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: DataSource
         * Reason: Overview entity data widget displaying LogicReport detail information
         */
        this.overviewWidget = WidgetFactory.createWidgetEntityData(
            'rp.config.page.logic.report.overview',
            'Uebersicht',
            'rp.RpLogicReport',
            logicReportId
        );

        const stepsFilter = [FilterCriteria.create('e.logicReportId', FilterCriteria.cOperatorEqual, logicReportId)];

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: DataProvider
         * Reason: Table widget listing pipeline steps filtered by parent LogicReport with FK resolution
         */
        this.stepsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'rp.config.page.logic.report.steps',
            'Pipeline Schritte',
            'rp.RpLogicReportStep',
            stepsFilter,
            [new Sorting('e.orderPriority', true)]
        );

        const stepCallbackFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'logicReportDtoId', fieldValue: logicReportId}
        ];
        this.stepsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(stepCallbackFields);

        this.mappingsWidget = null;

        const selectionParamsFilter = [
            FilterCriteria.create('e.logicReportId', FilterCriteria.cOperatorEqual, logicReportId)
        ];

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: DataProvider
         * Reason: Table widget listing selection parameters filtered by parent LogicReport with FK resolution
         */
        this.selectionParamsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'rp.config.page.logic.report.selection.params',
            'Selektionsparameter',
            'rp.RpLogicReportSelectionParam',
            selectionParamsFilter,
            [new Sorting('e.orderPriority', true)]
        );

        const selectionParamCallbackFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'logicReportDtoId', fieldValue: logicReportId}
        ];
        this.selectionParamsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(selectionParamCallbackFields);

        // Output Fields widget
        const outputFieldsFilter = [
            FilterCriteria.create('e.logicReportId', FilterCriteria.cOperatorEqual, logicReportId)
        ];

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: DataProvider
         * Reason: Table widget listing output fields filtered by parent LogicReport with FK resolution
         */
        this.outputFieldsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'rp.config.page.logic.report.output.fields',
            'Ausgabefelder',
            'rp.RpLogicReportOutputField',
            outputFieldsFilter,
            [new Sorting('e.orderPriority', true)]
        );

        const outputFieldCallbackFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'logicReportDtoId', fieldValue: logicReportId}
        ];
        this.outputFieldsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(outputFieldCallbackFields);
    }

    // ========================================================================
    // Pipeline Data Loading
    // ========================================================================

    /**
     * Load steps, mappings, selection params and output fields for the pipeline visualization
     * using a single batched QL query.
     */
    private loadPipelineData(): void {
        const logicReportId = this.objectIdentifier.objectId;
        this.pipelineLoading = true;

        const reportFilter = [FilterCriteria.create('e.logicReportId', FilterCriteria.cOperatorEqual, logicReportId)];

        this.qlService.builder()
            .query({
                name: 'steps',
                type: 'view',
                start: {type: 'entity', name: 'rp.RpLogicReportStep', as: 'e'},
                filters: reportFilter,
                sortings: [new Sorting('e.orderPriority', true)]
            })
            .query({
                name: 'mappings',
                type: 'view',
                start: {type: 'entity', name: 'rp.RpLogicReportStepMapping', as: 'e'},
                filters: reportFilter
            })
            .query({
                name: 'params',
                type: 'view',
                start: {type: 'entity', name: 'rp.RpLogicReportSelectionParam', as: 'e'},
                filters: reportFilter,
                sortings: [new Sorting('e.orderPriority', true)]
            })
            .query({
                name: 'outputFields',
                type: 'view',
                start: {type: 'entity', name: 'rp.RpLogicReportOutputField', as: 'e'},
                filters: reportFilter,
                sortings: [new Sorting('e.orderPriority', true)]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.pipelineSteps = result.get('steps') || [];
                    this.pipelineMappings = result.get('mappings') || [];
                    this.selectionParamEntries = result.get('params') || [];
                    this.outputFieldEntries = result.get('outputFields') || [];
                    this.buildPipelineGraph();
                    this.pipelineLoading = false;
                },
                error: () => {
                    this.pipelineLoading = false;
                }
            });
    }

    /**
     * Build the PipelineNode array from loaded steps and mappings.
     * Each node contains the step and its incoming mappings.
     */
    private buildPipelineGraph(): void {
        this.pipelineNodes = this.pipelineSteps.map(step => {
            const incomingMappings = this.pipelineMappings.filter(
                m => m.logicReportStepDtoId === step.id
            );
            return {
                step,
                incomingMappings,
                isSelected: false
            };
        });
    }

    /**
     * Select a pipeline node to show its detail panel.
     */
    selectPipelineNode(node: PipelineNode): void {
        // Deselect all first
        this.pipelineNodes.forEach(n => n.isSelected = false);

        if (this.selectedNode === node) {
            // Toggle off if already selected
            this.selectedNode = null;
            return;
        }

        node.isSelected = true;
        this.selectedNode = node;
    }

    /**
     * Close the pipeline detail panel.
     */
    deselectPipelineNode(): void {
        this.pipelineNodes.forEach(n => n.isSelected = false);
        this.selectedNode = null;
    }

    // ========================================================================
    // Step Widget Selection
    // ========================================================================

    handleStepSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        this.selectedStepId = event.objectId;

        const mappingsFilter = [
            FilterCriteria.create('e.logicReportStepId', FilterCriteria.cOperatorEqual, this.selectedStepId)
        ];

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: DataProvider
         * Reason: Table widget listing step mappings filtered by selected LogicReportStep with FK resolution
         */
        this.mappingsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'rp.config.page.logic.report.step.mappings',
            'Step Mappings',
            'rp.RpLogicReportStepMapping',
            mappingsFilter
        );

        const mappingCallbackFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'logicReportStepDtoId', fieldValue: this.selectedStepId}
        ];
        this.mappingsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(mappingCallbackFields);
    }

    // ========================================================================
    // Execution Logic (PRESERVED from existing component)
    // ========================================================================

    /**
     * Load the selection form metadata from the backend.
     * Called when the execution tab is activated.
     */
    loadSelectionForm(): void {
        if (this.selectionFormLoaded) {
            return;
        }

        this.selectionFormLoading = true;
        this.logicReportService.getSelectionForm(this.objectIdentifier.objectId).subscribe({
            next: (form: RpLogicReportSelectionFormDto) => {
                this.selectionForm = form;
                this.buildFormGroup(form.fields);
                this.selectionFormLoading = false;
                this.selectionFormLoaded = true;
            },
            error: () => {
                this.selectionFormLoading = false;
                this.selectionFormLoaded = true;
                this.selectionForm = null;
            }
        });
    }

    /**
     * FORM1 Exception: Manual FormGroup is required here because the selection parameter form
     * is a dynamic runtime-generated form whose fields are defined by backend configuration
     * (RpLogicReportSelectionParam entities). The field count, types, and names vary per report
     * and are unknown at compile time. MvsCrudObjectGenericComponent cannot be used because
     * these are transient execution parameters, not a persistent entity form.
     *
     * Build a reactive FormGroup from the selection form field definitions.
     * Each field becomes a FormControl with validators based on its metadata.
     */
    private buildFormGroup(fields: RpLogicReportSelectionFieldDto[]): void {
        const controls: { [key: string]: FormControl } = {};
        for (const field of fields) {
            const validators = field.mandatory ? [Validators.required] : [];
            controls[field.variableName] = new FormControl(field.defaultValue ?? null, validators);
        }
        this.selectionFormGroup = new FormGroup(controls);
    }

    /**
     * Execute the report pipeline with the current selection parameter values.
     * Calls the parameterized execution endpoint and displays structured results.
     */
    executeReport(): void {
        this.executionBusy = true;
        this.executionError = null;
        this.executionResult = null;
        this.exportResult = null;
        this.finalResultColumns = [];
        this.pipelineContextAliases = [];

        const hasParams = this.selectionForm && this.selectionForm.fields && this.selectionForm.fields.length > 0;

        if (hasParams) {
            if (this.selectionFormGroup.invalid) {
                this.executionBusy = false;
                return;
            }

            const params: { [key: string]: string } = {};
            for (const field of this.selectionForm.fields) {
                const value = this.selectionFormGroup.get(field.variableName)?.value;
                if (value !== null && value !== undefined && value !== '') {
                    params[field.variableName] = this.formatParamValue(field, value);
                }
            }

            this.logicReportService.executeWithParams(this.objectIdentifier.objectId, params).subscribe({
                next: (result: RpLogicReportExportResultDto) => {
                    this.exportResult = result;
                    this.executionBusy = false;
                },
                error: (error) => {
                    this.executionBusy = false;
                    this.executionError = error?.error?.message || 'Report-Ausfuehrung fehlgeschlagen';
                }
            });
        } else {
            this.logicReportService.executeReport(this.objectIdentifier.objectId).subscribe({
                next: (result: RpLogicReportExecutionResultDto) => {
                    this.executionResult = result;
                    this.executionBusy = false;
                    this.extractResultMetadata();
                },
                error: (error) => {
                    this.executionBusy = false;
                    this.executionError = error?.error?.message || 'Pipeline-Ausfuehrung fehlgeschlagen';
                }
            });
        }
    }

    /**
     * Export the report as Excel file with the current selection parameters.
     */
    exportExcel(): void {
        const params = this.collectParams();
        const obs = params
            ? this.logicReportService.executeExcelWithParams(this.objectIdentifier.objectId, params)
            : this.logicReportService.executeReportExcel(this.objectIdentifier.objectId);

        obs.subscribe({
            next: (blob: Blob) => {
                this.downloadBlob(blob, `${this.dto?.name || 'report'}.xlsx`);
            },
            error: (error) => {
                this.executionError = error?.error?.message || 'Excel-Export fehlgeschlagen';
            }
        });
    }

    /**
     * Export the report as CSV file with the current selection parameters.
     */
    exportCsv(): void {
        const params = this.collectParams();
        const obs = params
            ? this.logicReportService.executeCsvWithParams(this.objectIdentifier.objectId, params)
            : this.logicReportService.executeReportCsv(this.objectIdentifier.objectId);

        obs.subscribe({
            next: (blob: Blob) => {
                this.downloadBlob(blob, `${this.dto?.name || 'report'}.csv`);
            },
            error: (error) => {
                this.executionError = error?.error?.message || 'CSV-Export fehlgeschlagen';
            }
        });
    }

    /**
     * Collect the current form parameter values as a string map.
     * Returns null if no selection form is defined.
     */
    private collectParams(): { [key: string]: string } | null {
        if (!this.selectionForm?.fields?.length || !this.selectionFormGroup) {
            return null;
        }

        const params: { [key: string]: string } = {};
        for (const field of this.selectionForm.fields) {
            const value = this.selectionFormGroup.get(field.variableName)?.value;
            if (value !== null && value !== undefined && value !== '') {
                params[field.variableName] = this.formatParamValue(field, value);
            }
        }
        return params;
    }

    /**
     * Format a form control value to a string suitable for the backend.
     * Handles Date objects for date/dateTime controls.
     */
    private formatParamValue(field: RpLogicReportSelectionFieldDto, value: any): string {
        if (value instanceof Date) {
            if (field.uiInputControl === 'dateTime') {
                return value.toISOString();
            }
            // Format as YYYY-MM-DD for date fields
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            const day = String(value.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return String(value);
    }

    private extractResultMetadata(): void {
        const finalResult = this.executionResult?.finalResult;
        if (finalResult && Array.isArray(finalResult) && finalResult.length > 0) {
            this.finalResultColumns = Object.keys(finalResult[0]);
        }
        if (this.executionResult?.pipelineContext) {
            this.pipelineContextAliases = Object.keys(this.executionResult.pipelineContext);
        }
    }

    /**
     * Trigger a file download from a Blob.
     */
    private downloadBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
    }

    // ========================================================================
    // Status & Type Helper Methods
    // ========================================================================

    isPublished(): boolean {
        return this.dto?.reportStatus === RpLogicReportStatusEnum.PUBLISHED;
    }

    /**
     * Check whether the selection form has any fields that require user input.
     */
    hasSelectionFields(): boolean {
        return this.selectionForm?.fields?.length > 0;
    }

    /**
     * Returns PrimeNG tag severity for a report status value.
     */
    getReportStatusSeverity(status: RpLogicReportStatusEnum): 'success' | 'warn' | 'secondary' {
        switch (status) {
            case RpLogicReportStatusEnum.PUBLISHED:
                return 'success';
            case RpLogicReportStatusEnum.DRAFT:
                return 'warn';
            case RpLogicReportStatusEnum.ARCHIVED:
                return 'secondary';
            default:
                return 'secondary';
        }
    }

    /**
     * Returns a human-readable label for a report status value.
     */
    getReportStatusLabel(status: RpLogicReportStatusEnum): string {
        switch (status) {
            case RpLogicReportStatusEnum.PUBLISHED:
                return 'Veroeffentlicht';
            case RpLogicReportStatusEnum.DRAFT:
                return 'Entwurf';
            case RpLogicReportStatusEnum.ARCHIVED:
                return 'Archiviert';
            default:
                return 'Unbekannt';
        }
    }

    /**
     * Returns the PrimeNG icon class for a step type.
     */
    getStepTypeIcon(stepType: RpLogicReportStepTypeEnum): string {
        switch (stepType) {
            case RpLogicReportStepTypeEnum.QL_QUERY:
                return 'fa-regular fa-database';
            case RpLogicReportStepTypeEnum.LG_LOGIC:
                return 'fa-regular fa-code';
            default:
                return 'fa-regular fa-gear';
        }
    }

    /**
     * Returns a human-readable label for a step type.
     */
    getStepTypeLabel(stepType: RpLogicReportStepTypeEnum): string {
        switch (stepType) {
            case RpLogicReportStepTypeEnum.QL_QUERY:
                return 'QL Query';
            case RpLogicReportStepTypeEnum.LG_LOGIC:
                return 'Logic';
            default:
                return 'Unbekannt';
        }
    }

    /**
     * Returns the maximum execution time across all step executions (for timeline scaling).
     */
    getMaxExecutionTime(): number {
        if (!this.executionResult?.stepExecutions?.length) {
            return 1;
        }
        return Math.max(...this.executionResult.stepExecutions.map(s => s.executionTimeMs), 1);
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
